/**
 * @fileoverview SessionViewerPool Component
 * 
 * Renders all SessionViewer instances into react-reverse-portal InPortals.
 * This component is separated to isolate re-renders when the sessions object changes.
 * 
 * **Why This Component Exists:**
 * 
 * The SessionViewerPool subscribes to the entire `sessions` object from Zustand.
 * When any session property changes (e.g., noise, handRaised, unreadMessageCount),
 * Zustand creates a new object reference, triggering a re-render.
 * 
 * By isolating this subscription in a separate component, we prevent the parent
 * SessionViewerProvider from re-rendering, which would reset tab state and other UI.
 * 
 * **How React-Reverse-Portal Works Here:**
 * 
 * Each SessionViewer is rendered into an InPortal with a stable portal node.
 * React's reconciliation with `key={sessionSid}` ensures:
 * - Existing SessionViewer components are REUSED (same key = same instance)
 * - New sessions trigger component CREATION
 * - Removed sessions trigger component UNMOUNTING
 * 
 * The InPortal renders components into detached DOM nodes that can be moved
 * anywhere using OutPortals without remounting or losing state.
 * 
 * @module vtc/contexts/SessionViewerContext/SessionViewerPool
 * @private
 */

import React, { useEffect } from 'react';
import * as portals from 'react-reverse-portal';
import { SessionViewer } from '@shared/components/proctoring/SessionViewer';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';
import { getEnabledComponentsForSession } from './utils';

/**
 * SessionViewerPool Component
 * 
 * Renders all SessionViewer instances into InPortals. This component
 * subscribes to the sessions object and will re-render when it changes, but
 * it's isolated so it doesn't cause the entire provider tree to re-render.
 * 
 * @component
 * @param {Object} props
 * @param {React.MutableRefObject} props.portalNodesRef - Ref object storing portal nodes
 * @param {Function} props.getOrCreatePortalNode - Function to get/create portal node for a session
 * @returns {JSX.Element}
 * 
 * @example
 * <SessionViewerPool
 *   portalNodesRef={portalNodesRef}
 *   getOrCreatePortalNode={getOrCreatePortalNode}
 * />
 */
export function SessionViewerPool({ portalNodesRef, getOrCreatePortalNode }) {
    // Subscribe to sessions object - this component will re-render when sessions change
    // But it won't cause the parent SessionViewerProvider to re-render
    const sessions = useSessionStore((state) => state.sessions);

    // ALSO subscribe to focusedSessionSid so we re-render when focus changes
    // This ensures getEnabledComponentsForSession() is called with the correct focused session
    const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);

    // Subscribe to visibleSessions to keep them in the pool
    const visibleSessions = useSessionStore((state) => state.visibleSessions);

    // Get audio device IDs from app store
    const audioDevices = useAppStore((state) => state.audioDevices);

    // Get current room SID for setting attachedToProctorRoomSid
    const roomSid = useAppStore((state) => state.roomSid);

    // Set attachedToProctorRoomSid for each session when viewerRef becomes available
    useEffect(() => {
        if (!roomSid) return;

        Object.keys(sessions).forEach((sessionSid) => {
            const session = sessions[sessionSid];
            const viewerRef = session?.viewerRef;

            // Wait for viewerRef to be available
            if (viewerRef?.current?.setAttachedToProctorRoomSid) {
                viewerRef.current.setAttachedToProctorRoomSid(roomSid);
            }
        });
    }, [sessions, roomSid]);

    // PR-3412: Update viewMode for each session based on focus state
    // This prevents cross-talk by ensuring only focused session has viewMode='focus'
    useEffect(() => {
        Object.keys(sessions).forEach((sessionSid) => {
            const session = sessions[sessionSid];
            const viewerRef = session?.viewerRef;

            // Calculate viewMode based on focus state
            const isFocused = sessionSid === focusedSessionSid;
            const forcedViewMode = isFocused ? 'focus' : 'overview';

            // Wait for viewerRef to be available, then update viewMode imperatively
            if (viewerRef?.current?.setViewMode) {
                viewerRef.current.setViewMode(forcedViewMode);
            }
        });
    }, [sessions, focusedSessionSid]);

    return (
        <>
            {/* 
                Render all SessionViewer instances into InPortals.
                
                IMPORTANT: React's reconciliation with key={sessionSid} ensures:
                - Existing sessions are REUSED (same key = same component instance)
                - New sessions are CREATED
                - Removed sessions are UNMOUNTED
                
                InPortals render components into detached DOM nodes that persist
                across moves. LiveKit connections, component state, and event listeners
                are preserved because components never remount.
                
                POOL FILTERING: Only keeps sessions that are:
                - In 'testing' or 'review' locations (active sessions that need live connections)
                - Currently focused (to maintain state when viewing)
                Other sessions (waiting, checkout, reconnect, sentback) are unmounted to save resources.
            */}
            {Object.keys(sessions).map((sessionSid) => {
                const session = sessions[sessionSid];
                const viewerProps = session?.viewerProps;
                const viewerRef = session?.viewerRef;

                // Skip if no viewer props configured
                if (!viewerProps) return null;

                // Check if session is in visibleSessions array
                const isInVisibleSessions = visibleSessions.some(
                    (visibleSession) => visibleSession.sessionSid === sessionSid
                );

                // Only keep sessions in testing/review locations, if focused, or if in visibleSessions
                const shouldKeepInPool =
                    session.location === 'testing' ||
                    session.location === 'review' ||
                    sessionSid === focusedSessionSid ||
                    isInVisibleSessions;

                if (!shouldKeepInPool) {
                    // Session not in pool - React will handle unmounting cleanly
                    return null;
                }

                // Get session-specific configuration overrides
                const sessionConfig = getEnabledComponentsForSession(sessionSid);

                // Merge default enabled components with session-specific overrides
                const enabledComponents = {
                    ...viewerProps.enabledComponents, // Defaults from session store
                    ...sessionConfig // Session-specific overrides (without video)
                };

                // Extract video config separately to merge at the top level
                const { video: sessionVideo, ...sessionConfigWithoutVideo } = sessionConfig;
                const mergedEnabledComponents = {
                    ...viewerProps.enabledComponents,
                    ...sessionConfigWithoutVideo
                };

                // Merge video configuration
                const videoConfig = sessionVideo || viewerProps.video;

                // Get or create portal node for this session
                const portalNode = getOrCreatePortalNode(sessionSid);

                return (
                    <portals.InPortal key={sessionSid} node={portalNode}>
                        <SessionViewer
                            ref={viewerRef}
                            {...viewerProps}
                            enabledComponents={mergedEnabledComponents}
                            video={videoConfig}
                            audioInputDeviceId={audioDevices?.inputDeviceId}
                            audioOutputDeviceId={audioDevices?.outputDeviceId}
                        />
                    </portals.InPortal>
                );
            })}
        </>
    );
}
